import * as React from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Download, FileText, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { onWriteError } from "@/lib/writeErrors"

import {
  descargarBoletas,
  fetchEstadoDeBoletas,
} from "@/routes/payroll/api"

/**
 * DESCARGAR LAS BOLETAS DE PAGO.
 *
 * Punto 10b de Cesar: «poder descargar las boletas de pago».
 *
 * ---------------------------------------------------------------------------
 * LO QUE FALTA SE DICE ANTES DE DESCARGAR, NO DESPUÉS DE IMPRIMIR
 *
 * En Perú la boleta es un documento con contenido obligatorio (D.S. 001-98-TR)
 * y Marty todavía no tiene el cargo ni los días trabajados. El PDF se genera
 * igual, marcado como BORRADOR, pero descubrirlo después de mandar veintiocho
 * páginas a la impresora no sirve de nada: lo que falta es un dato del maestro
 * que se carga en cinco minutos, y decirlo antes convierte una impresión
 * perdida en una tarea.
 *
 * Por eso este panel consulta `/boletas/estado` y enseña lo que falta AGRUPADO
 * —«a las 28 les falta el cargo»— en vez de repetir veintiocho veces la misma
 * línea. Una lista de veintiocho filas idénticas esconde que se arregla con un
 * solo dato.
 *
 * ---------------------------------------------------------------------------
 * EL BOTÓN NO SE DESACTIVA
 *
 * Aunque estén todas en borrador. Un borrador sirve: se revisa, se pasa al
 * área de personas, se usa para cuadrar. Lo que no puede pasar es que alguien
 * lo confunda con el documento final, y de eso se encarga la banda roja
 * impresa en el propio PDF — no un botón gris que sólo enseña que no se puede.
 */
export function BoletasPanel({
  anio, mes, periodo,
}: {
  anio: number
  mes: number
  periodo: string
}) {
  const { activeTenantId, activeEntityId } = useAuth()
  const { toast } = useToast()

  const q = useQuery({
    queryKey: ["payroll-boletas", activeTenantId, activeEntityId, anio, mes],
    queryFn: () => fetchEstadoDeBoletas(anio, mes, activeEntityId),
    enabled: Boolean(activeTenantId) && Boolean(activeEntityId),
  })

  const d = q.data

  const bajar = useMutation({
    mutationFn: (formato: "pdf" | "html") =>
      descargarBoletas(anio, mes, activeEntityId!, { formato }),
    onSuccess: () => toast({ title: t("Boletas descargadas") }),
    onError: onWriteError("las boletas", "descargar"),
  })

  return (
    <WorkPanel
      title={t("Boletas de pago")}
      meta={
        d ? (
          <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <FileText className="h-3 w-3" aria-hidden="true" />
            {d.personas} {t("página(s)")}
            {d.borradores > 0 ? (
              <Badge variant="warning-soft" estado>
                <TriangleAlert />
                {d.borradores} {t("en borrador")}
              </Badge>
            ) : (
              <Badge variant="success-soft" estado>{t("entregables")}</Badge>
            )}
          </span>
        ) : null
      }
      actions={
        <div className="flex gap-1.5">
          <Button
            size="sm"
            onClick={() => bajar.mutate(d?.formato_pdf === false ? "html" : "pdf")}
            disabled={!activeEntityId || bajar.isPending || !d?.personas}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {d?.formato_pdf === false ? t("Descargar para imprimir") : t("Descargar PDF")}
          </Button>
        </div>
      }
      footnote={d?.nota}
    >
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={Boolean(d) && d?.personas === 0}
        emptyMessage={t("No hay nadie en planilla este mes, así que no hay boletas.")}
        onRetry={() => void q.refetch()}
        rows={3}
      >
        <div className="space-y-2 p-2.5">
          {/*
            SI NO ES UN DOCUMENTO LEGAL, SE DICE PRIMERO. Un PDF con aspecto de
            boleta emitido para México se archiva como si valiera, y el recibo
            de nómina mexicano es un CFDI timbrado por el SAT: un XML con
            sello, no un papel que generemos nosotros.
          */}
          {d && !d.documento_legal ? (
            <p className="flex items-start gap-1.5 rounded-md border border-warning/45 bg-warning-soft px-2.5 py-2 text-2xs leading-5">
              <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
              {t("Esto es un detalle de planilla, no la boleta legal de este país. No sustituye al documento que exige la autoridad.")}
            </p>
          ) : null}

          {d && d.formato_pdf === false ? (
            <p className="text-2xs leading-4 text-muted-foreground">
              {t("Este servidor no puede generar PDF, así que se descarga una página lista para imprimir. Lleva exactamente el mismo contenido.")}
            </p>
          ) : null}

          {d && d.que_falta.length ? (
            <div>
              <p className="text-2xs font-medium">
                {t("Para que se puedan entregar, falta cargar")}
              </p>
              <ul className="mt-1 space-y-0.5">
                {d.que_falta.map((f) => (
                  <li key={f.texto} className="flex gap-2 text-2xs leading-4">
                    {/*
                      EL RECUENTO DELANTE DEL TEXTO. «28 × el cargo» se lee de
                      un vistazo como «esto lo arreglo una vez y se arreglan
                      las 28»; el texto primero obliga a leer la frase entera
                      antes de saber a cuántas afecta.
                    */}
                    <span className="num-strong shrink-0 tabular-nums">
                      {f.personas}×
                    </span>
                    <span className="text-muted-foreground">{f.texto}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-2xs leading-4 text-muted-foreground">
                {t("Mientras falte algo, cada página sale con una banda que dice BORRADOR — NO ENTREGABLE. Se puede usar para revisar; no para entregar.")}
              </p>
            </div>
          ) : (
            <p className="text-2xs leading-4 text-muted-foreground">
              {t("No falta nada: las boletas salen completas y con espacio para la firma.")}
            </p>
          )}
        </div>
      </DataState>
    </WorkPanel>
  )
}
